import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setUnauthorizedHandler } from '../db/database';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState('loading'); // loading | anon | auth
  const [user, setUser] = useState(null);

  /* Au démarrage : l'application est-elle déjà connectée ? */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/status', { credentials: 'same-origin' });
        const data = await res.json();
        if (cancelled) return;
        if (data && data.authenticated) {
          setUser(data.user);
          setStatus('auth');
        } else {
          setStatus('anon');
        }
      } catch (error) {
        // Serveur injoignable : on affiche la page de connexion.
        if (!cancelled) setStatus('anon');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Session expirée en cours d'utilisation (401) → retour page de connexion. */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('anon');
    });
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Échec de la connexion');
    setUser(data.user);
    setStatus('auth');
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (error) {
      // Même hors-ligne, on déconnecte la vue.
    }
    setUser(null);
    setStatus('anon');
  }, []);

  const value = { status, user, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
